import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { ButtonV2 } from "../ui/ButtonV2";
import { ThemeToggle } from "../../ThemeToggle";
import { Container } from "../shared/Container";
import { Logo } from "../shared/Logo";
import { cn } from "@/lib/utils";
import { getAllSolutions } from "@/lib/content";
import { useFocusTrap } from "@/hooks/useFocusTrap";

const solutions = getAllSolutions();

function isActivePath(location, path) {
  if (path === "/") return location.pathname === "/";
  return (
    location.pathname === path || location.pathname.startsWith(`${path}/`)
  );
}

export function NavbarV2() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const location = useLocation();
  const mobileMenuRef = useRef(null);
  const menuButtonRef = useRef(null);

  useFocusTrap(isMobileOpen, mobileMenuRef);

  const isSolutionsActive = location.pathname.startsWith("/solutions");

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
    setSolutionsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isMobileOpen) {
      document.body.classList.add("v2-nav-open");
    } else {
      document.body.classList.remove("v2-nav-open");
    }
    return () => document.body.classList.remove("v2-nav-open");
  }, [isMobileOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setIsMobileOpen(false);
        setSolutionsOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    if (isMobileOpen) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isMobileOpen]);

  useEffect(() => {
    if (location.pathname === "/" && location.hash) {
      const sectionId = location.hash.replace("#", "");
      const scrollToSection = () => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
          return true;
        }
        return false;
      };

      if (!scrollToSection()) {
        const retryTimer = setTimeout(scrollToSection, 300);
        return () => clearTimeout(retryTimer);
      }
    }
  }, [location.pathname, location.hash]);

  const navLinkClass = (path) =>
    cn(
      "px-3 py-2 text-sm font-medium transition-colors rounded-v2-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand",
      isActivePath(location, path)
        ? "text-brand"
        : "text-v2-foreground hover:text-brand"
    );

  const navLinkProps = (path) =>
    isActivePath(location, path) ? { "aria-current": "page" } : {};

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:rounded-v2-md focus:bg-brand focus:px-4 focus:py-2 focus:text-white"
      >
        Skip to content
      </a>

      <header>
        <nav
          className={cn(
            "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
            isScrolled || isMobileOpen
              ? "border-b border-brand/25 bg-v2-surface/90 shadow-v2-glow backdrop-blur-md"
              : "bg-transparent"
          )}
          aria-label="Main navigation"
        >
          <Container>
            <div className="flex items-center justify-between h-16 lg:h-18">
              <Link
                to="/"
                className="flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded-v2-sm"
                aria-label="Creativorare — go to homepage"
                {...(location.pathname === "/" ? { "aria-current": "page" } : {})}
              >
                <Logo className="h-8 lg:h-10 w-auto" />
              </Link>

              <div className="hidden lg:flex items-center gap-1">
                <div className="relative group">
                  <button
                    type="button"
                    className={cn(
                      "flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors rounded-v2-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand",
                      isSolutionsActive
                        ? "text-brand"
                        : "text-v2-foreground hover:text-brand"
                    )}
                    aria-haspopup="true"
                    aria-expanded={isSolutionsActive}
                  >
                    Solutions
                    <ChevronDown className="w-4 h-4 opacity-60" aria-hidden="true" />
                  </button>
                  <div className="absolute top-full left-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-all duration-200">
                    <ul
                      className="w-72 rounded-v2-lg border border-v2-border bg-v2-surface shadow-v2-lg p-2 list-none m-0"
                      role="menu"
                      aria-label="Solutions"
                    >
                      {solutions.map((solution) => (
                        <li key={solution.id} role="none">
                          <Link
                            to={`/solutions/${solution.id}`}
                            role="menuitem"
                            className="block px-3 py-2 text-sm text-v2-foreground hover:bg-v2-background hover:text-brand rounded-v2-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                            {...(location.pathname === `/solutions/${solution.id}`
                              ? { "aria-current": "page" }
                              : {})}
                          >
                            {solution.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <Link to="/work" className={navLinkClass("/work")} {...navLinkProps("/work")}>
                  Work
                </Link>
                <Link
                  to="/company/about"
                  className={navLinkClass("/company/about")}
                  {...navLinkProps("/company/about")}
                >
                  About
                </Link>
                <Link
                  to="/company/contact"
                  className={navLinkClass("/company/contact")}
                  {...navLinkProps("/company/contact")}
                >
                  Contact
                </Link>

                <div className="ml-2 pl-2 border-l border-v2-border">
                  <ThemeToggle />
                </div>

                <ButtonV2 size="default" className="ml-3" asChild>
                  <Link to="/company/contact">Book Consultation</Link>
                </ButtonV2>
              </div>

              <div className="flex lg:hidden items-center gap-1">
                <ThemeToggle />
                <button
                  ref={menuButtonRef}
                  type="button"
                  onClick={() => setIsMobileOpen(!isMobileOpen)}
                  className="v2-touch-target flex items-center justify-center p-2 rounded-v2-md hover:bg-v2-background transition-colors touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                  aria-label={isMobileOpen ? "Close menu" : "Open menu"}
                  aria-expanded={isMobileOpen}
                  aria-controls="mobile-navigation"
                >
                  {isMobileOpen ? (
                    <X className="w-5 h-5 text-v2-foreground" aria-hidden="true" />
                  ) : (
                    <Menu className="w-5 h-5 text-v2-foreground" aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>
          </Container>

          <div
            id="mobile-navigation"
            ref={mobileMenuRef}
            className={cn(
              "lg:hidden overflow-hidden transition-all duration-300",
              isMobileOpen
                ? "max-h-[85dvh] opacity-100 overflow-y-auto"
                : "max-h-0 opacity-0"
            )}
            aria-hidden={!isMobileOpen}
          >
            <div className="border-t border-v2-border bg-v2-surface">
              <Container className="py-3 space-y-0.5">
                <button
                  type="button"
                  onClick={() => setSolutionsOpen(!solutionsOpen)}
                  className="flex w-full items-center justify-between min-h-[48px] py-3 px-3 text-sm font-medium text-v2-foreground hover:text-brand rounded-v2-sm touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                  aria-expanded={solutionsOpen}
                  aria-controls="mobile-solutions-list"
                >
                  Solutions
                  <ChevronDown
                    className={cn(
                      "w-4 h-4 transition-transform",
                      solutionsOpen && "rotate-180"
                    )}
                    aria-hidden="true"
                  />
                </button>
                {solutionsOpen && (
                  <ul
                    id="mobile-solutions-list"
                    className="pl-3 space-y-0.5 pb-2 max-h-52 overflow-y-auto list-none m-0"
                  >
                    {solutions.map((solution) => (
                      <li key={solution.id}>
                        <Link
                          to={`/solutions/${solution.id}`}
                          className="flex items-center min-h-[44px] py-2 px-3 text-sm text-v2-muted hover:text-brand rounded-v2-sm touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                          {...(location.pathname === `/solutions/${solution.id}`
                            ? { "aria-current": "page" }
                            : {})}
                        >
                          {solution.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
                <Link
                  to="/work"
                  className="flex items-center min-h-[48px] py-3 px-3 text-sm font-medium text-v2-foreground hover:text-brand rounded-v2-sm touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                  {...navLinkProps("/work")}
                >
                  Work
                </Link>
                <Link
                  to="/company/about"
                  className="flex items-center min-h-[48px] py-3 px-3 text-sm font-medium text-v2-foreground hover:text-brand rounded-v2-sm touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                  {...navLinkProps("/company/about")}
                >
                  About
                </Link>
                <Link
                  to="/company/contact"
                  className="flex items-center min-h-[48px] py-3 px-3 text-sm font-medium text-v2-foreground hover:text-brand rounded-v2-sm touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                  {...navLinkProps("/company/contact")}
                >
                  Contact
                </Link>
                <div className="pt-3 pb-2">
                  <ButtonV2 className="w-full" asChild>
                    <Link to="/company/contact">Book Consultation</Link>
                  </ButtonV2>
                </div>
              </Container>
            </div>
          </div>
        </nav>
      </header>
    </>
  );
}
